/**
 * Dati delle novità dell'applicazione.
 * Aggiungi nuove release in cima all'array.
 *
 * Struttura:
 * - version: deve corrispondere alla versione in version.json
 * - date: data di rilascio (YYYY-MM-DD)
 * - titleKey: chiave i18n per il titolo (es. 'WHATS_NEW.RELEASES.V2_1_0.TITLE')
 * - title: titolo fallback se la traduzione non esiste
 * - highlightKeys: array di chiavi i18n per gli highlights
 * - highlights: array di fallback se le traduzioni non esistono
 * - images: array di oggetti immagine { url, alt, highlightIndex } (opzionale)
 *           highlightIndex indica dopo quale highlight mostrare l'immagine (0-based)
 * - videoUrl: URL del video esplicativo (opzionale) - mostra un bottone "Guarda il video"
 * - type: 'major' | 'minor' | 'patch' (opzionale, per styling)
 * - enabled: true | false (opzionale, default true) - se false la release non viene mostrata
 */

export const WHATS_NEW_RELEASES = [
  {
    version: '2.7.0',
    enabled: false,
    date: '2026-04-08',
    titleKey: 'WHATS_NEW.RELEASES.V2_7_0.TITLE',
    title: 'Cerca nello storico completo delle tue email',
    type: 'minor',
    highlightKeys: [
      'WHATS_NEW.RELEASES.V2_7_0.HIGHLIGHTS.INTRO',
      'WHATS_NEW.RELEASES.V2_7_0.HIGHLIGHTS.ACTIVATE',
      'WHATS_NEW.RELEASES.V2_7_0.HIGHLIGHTS.INBOX_SELECT',
      'WHATS_NEW.RELEASES.V2_7_0.HIGHLIGHTS.PROVIDERS',
      'WHATS_NEW.RELEASES.V2_7_0.HIGHLIGHTS.IMPORT',
    ],
    highlights: [
      'Ora puoi cercare tra tutte le email mai ricevute o inviate, direttamente dalla barra di ricerca di Otello. Attiva la ricerca avanzata per accedere allo storico completo della tua casella.',
      "Come attivarla — Clicca sull'icona filtro a destra nella barra di ricerca per attivare la ricerca avanzata.",
      'Scegli la casella — Se hai più caselle email collegate, puoi scegliere su quale cercare oppure cercare su tutte contemporaneamente.',
      'Compatibile con tutti i provider — Funziona con Gmail, Outlook e qualsiasi casella IMAP (Aruba, Libero, ecc.).',
      'Importa con un click — Clicca su un risultato per importarlo direttamente in Otello e gestirlo come una normale conversazione.',
    ],
    images: [
      {
        url: 'https://storage.googleapis.com/template-otello/update-otello/ricerca-storica-mail.png',
        alt: 'Ricerca avanzata nello storico email',
        highlightIndex: 1,
      },
    ],
  },
  // Aggiungi nuove release qui sopra
];

/**
 * Controlla se una release è abilitata (default true se non specificato)
 */
function isReleaseEnabled(release) {
  return release?.enabled !== false;
}

/**
 * Restituisce solo le release abilitate
 */
function getEnabledReleases() {
  return WHATS_NEW_RELEASES.filter(isReleaseEnabled);
}

/**
 * Restituisce la versione più recente disponibile (tra quelle abilitate)
 */
export function getLatestVersion() {
  const enabledReleases = getEnabledReleases();
  return enabledReleases[0]?.version || null;
}

/**
 * Restituisce tutte le novità non ancora viste dall'utente (solo quelle abilitate)
 * @param {string} lastSeenVersion - L'ultima versione vista dall'utente
 * @returns {Array} Array di release non viste e abilitate
 */
export function getUnseenReleases(lastSeenVersion) {
  const enabledReleases = getEnabledReleases();

  if (!lastSeenVersion) {
    // Prima visita: mostra solo l'ultima release abilitata
    return enabledReleases.slice(0, 1);
  }

  const lastSeenIndex = enabledReleases.findIndex(
    (r) => r.version === lastSeenVersion
  );

  if (lastSeenIndex === -1) {
    // Versione non trovata, mostra tutte le abilitate
    return enabledReleases;
  }

  if (lastSeenIndex === 0) {
    // Già aggiornato
    return [];
  }

  // Ritorna tutte le release abilitate più recenti di quella vista
  return enabledReleases.slice(0, lastSeenIndex);
}

/**
 * Controlla se ci sono novità non viste
 * @param {string} lastSeenVersion - L'ultima versione vista dall'utente
 * @returns {boolean}
 */
export function hasUnseenReleases(lastSeenVersion) {
  const latestVersion = getLatestVersion();

  if (!latestVersion) return false;
  if (!lastSeenVersion) return true;

  return lastSeenVersion !== latestVersion;
}

export default {
  WHATS_NEW_RELEASES,
  getLatestVersion,
  getUnseenReleases,
  hasUnseenReleases,
};
